import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatWibJam, formatWibTanggal, type Camera, type Timeline, type TimelineSegment } from '../lib/api';
import { cocokKamera } from '../lib/cariKamera';
import { RETENSI_MAX_HARI } from '../lib/retensi';
import { KendaliSkala } from '../components/KendaliSkala';
import { faktorRodaZoom } from '../lib/zoomGambar';
import { useLayarLebar } from '../hooks/useLayarLebar';
import { useBahasa } from '../i18n';

/**
 * Playback multi-kamera (§G10.9) — **satu timeline, banyak sudut**.
 *
 * **Hanya di layar lebar (≥1024px).** Diminta lapangan 8 Sep 2026: *"di mode
 * hape kok ada mode multi camera ya? Harusnya kan nggak ada. Soalnya di mobile
 * jelek banget kelihatannya."* Satu kolom selebar 360px membagi tinggi layar
 * untuk empat gambar sekaligus penggaris, empat pita, dan transport — tiap
 * kamera kebagian ±90px, dan pada tinggi segitu tidak ada satu pun pertanyaan
 * yang bisa dijawab dengan melihatnya. Pintunya ditutup di `/videos`, dan
 * halaman ini menolak dengan sopan kalau tetap dibuka lewat URL atau lewat
 * layar yang baru saja diputar.
 *
 * Halaman sendiri, bukan mode di `/videos`, dan itu keputusan sadar: playback
 * satu kamera adalah halaman yang dipakai setiap hari oleh orang yang sedang
 * mencari bukti, dan satu bug di sini tidak boleh menjatuhkannya.
 *
 * ## Arah jam dibalik, dan itu perbedaan yang paling penting
 *
 * Di `/videos` posisi playhead dibaca **dari** elemen video (`onTimeUpdate`) —
 * boleh, karena di sana elemen itu satu-satunya sumber waktu yang ada. Dengan
 * empat kamera tidak ada elemen yang berhak jadi jam: kamera yang sedang berada
 * di bolongnya sendiri tidak punya waktu untuk dilaporkan, dan menjadikannya
 * acuan berarti tiga kamera lain ikut berhenti.
 *
 * Jadi di sini jamnya berdiri sendiri (`posMs`, dimajukan `requestAnimationFrame`
 * menurut jam dinding × laju), dan **elemen video yang mengejar jam**, bukan
 * sebaliknya. Kamera yang tidak punya rekaman pada detik itu menampilkan
 * kekosongan yang jujur, dan jam terus berjalan.
 *
 * ## Satu elemen per kamera, bukan dua
 *
 * `/videos` memakai dua elemen bergantian supaya pergantian berkas tidak
 * berkedip hitam. Di sini sengaja **tidak**: empat kamera × dua elemen = delapan
 * pemuatan serentak, naik ke enam belas saat pra-muat, dan itu sudah melewati
 * batas koneksi yang pernah menggigit kita di tile MJPEG. Ongkosnya jujur:
 * **ada kedipan di batas berkas tiap 15 menit**. Untuk membandingkan empat sudut
 * pada satu detik yang sama, itu harga yang jauh lebih murah daripada empat
 * pemutar yang saling berebut koneksi.
 *
 * ## Yang sengaja TIDAK ada di sini
 *
 * Ekspor, bookmark, mark in/out, dan zoom gambar. Semuanya tinggal di halaman
 * satu kamera. Halaman ini menjawab satu pertanyaan — *"pada detik ini, apa yang
 * terlihat dari sudut lain?"* — dan tiap kendali yang tidak melayani pertanyaan
 * itu cuma memperbesar permukaan yang bisa rusak.
 *
 * ## Yang ternyata BUKAN kemewahan, dan sejak 8 Sep 2026 ada di sini
 *
 * Daftar di atas dulu ikut menelan maju-mundur cepat, zoom sumbu, dan papan
 * ketik — dan itu salah membaca batasnya sendiri. Dilaporkan lapangan: *"di
 * mode multicam, kenapa malah nggak ada fungsi-fungsi dasar yang sudah kita
 * bicarakan sebelumnya ya?"* Benar: ekspor dan bookmark memang milik halaman
 * satu kamera, tapi **menyusuri waktu** bukan fitur tambahan — itu justru satu-
 * satunya cara sampai ke detik yang mau dibandingkan. Sekarang halaman ini
 * punya ⏪/⏩ bertingkat, sumbu yang bisa di-zoom dan digeser (roda, cubitan,
 * tombol), dan peta tombol yang sama dengan `/videos` sejauh yang masuk akal di
 * sini.
 */

/** Sama dengan `/videos`. 30D = `RETENSI_MAX_HARI` (§D6). */
const RENTANG_CEPAT = [
  { id: '1H', jam: 1 },
  { id: '6H', jam: 6 },
  { id: '24H', jam: 24 },
  { id: '7D', jam: 24 * 7 },
  { id: '30D', jam: 24 * RETENSI_MAX_HARI },
] as const;
type IdRentang = (typeof RENTANG_CEPAT)[number]['id'];

/**
 * Empat kamera, dan tidak lebih.
 *
 * Bukan angka selera: 3×3 berarti sembilan elemen `<video>` yang men-dekode
 * H.264 1080p bersamaan di browser pos satpam, dan sembilan berkas yang ditarik
 * serentak dari NFS pada tiap batas segmen. Naikkan angka ini hanya setelah
 * keduanya diukur di perangkat yang sungguh dipakai.
 */
const MAKS_KAMERA = 4;

/**
 * Laju dibatasi 8× di sini, bukan 32× seperti satu kamera.
 *
 * Empat dekoder yang dipacu bersamaan berarti empat kali beban, dan yang
 * pertama menyerah biasanya bukan yang sedang diperhatikan orang — hasilnya
 * empat sudut yang tidak lagi menunjuk detik yang sama, yaitu satu-satunya
 * janji halaman ini.
 */
const LAJU = [1, 2, 4, 8];

/** Selisih maksimum antara elemen dan jam sebelum ia dipaksa mengejar. */
const AMBANG_KEJAR_S = 0.75;

/**
 * Jendela tersempit yang boleh dicapai zoom.
 *
 * Satu menit, sama dengan `/videos`: di bawah itu penggarisnya kehabisan angka
 * bulat untuk ditulis, dan satu piksel jadi lebih halus daripada ambang kejar
 * elemen videonya sendiri — sumbu yang menjanjikan ketepatan yang tidak dimiliki
 * gambarnya.
 */
const JENDELA_MIN_MS = 60_000;

/** Seretan sependek ini bukan seretan, melainkan klik yang tangannya bergoyang. */
const GESER_MINIMAL_PX = 4;

/**
 * Peta tombol halaman ini.
 *
 * Sengaja **bukan** salinan utuh dari `/videos`: yang disebut di sini cuma yang
 * benar-benar berlaku, karena peta tombol yang menjanjikan `M` di halaman tanpa
 * bookmark adalah dokumentasi yang salah — dan dokumentasi yang salah lebih
 * mahal daripada yang tidak ada.
 */
const PETA_TOMBOL: [string, string][] = [
  ['Space', 'Play / pause'],
  ['L', 'Forward — press again for 2×, 4×, 8×'],
  ['J', 'Rewind — press again for 2×, 4×, 8×'],
  ['K', 'Stop, back to 1×'],
  ['→ / ←', 'Step one second'],
  ['Shift + → / ←', 'Step ten seconds'],
  ['+ / −', 'Zoom the timeline'],
  ['Home / End', 'To the visible edge — press again to page one screen'],
  ['?', 'Show or hide this list'],
];

const KUNCI_SIMPAN = 'lensa.multiplayback.kamera';

/**
 * Posisi tik penggaris, sebagai pecahan dari lebar sumbu.
 *
 * Tujuh tik dan bukan angka yang dihitung dari lebar layar: label jam WIB
 * ("03:15") butuh ±40 px, dan pada layar tersempit yang ditargetkan halaman ini
 * tujuh label masih muat tanpa bertumpuk. Ujung kiri dan kanan sengaja ikut —
 * yang pertama dicari orang saat melihat sumbu adalah di mana ia mulai dan
 * berakhir.
 */
const TIK = [0, 1 / 6, 2 / 6, 0.5, 4 / 6, 5 / 6, 1];

function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function ujungHari(tanggal: string): number {
  const [y, m, d] = tanggal.split('-').map(Number);
  if (!y || !m || !d) return Date.now();
  return Math.min(new Date(y, m - 1, d, 23, 59, 59, 999).getTime(), Date.now());
}

/** Segmen yang memuat jam dinding `ms`, atau `null` kalau sedang di bolong. */
function segmenPada(segments: TimelineSegment[], ms: number): TimelineSegment | null {
  for (const sg of segments) {
    const mulai = new Date(sg.startedAt).getTime();
    if (ms >= mulai && ms < mulai + sg.durationS * 1000) return sg;
  }
  return null;
}

/* ── Satu ubin ────────────────────────────────────────────────────────────── */

/**
 * Satu kamera di dalam grid. Ia **tidak pernah** melaporkan waktu ke atas —
 * satu-satunya arah datanya turun: jam → elemen.
 */
function Ubin({
  kamera,
  segments,
  posMs,
  main,
  rate,
  onLepas,
}: {
  kamera: Camera;
  segments: TimelineSegment[];
  posMs: number;
  main: boolean;
  rate: number;
  onLepas: () => void;
}) {
  const { t } = useBahasa();
  const vidRef = useRef<HTMLVideoElement>(null);
  /** Segmen yang sedang terpasang di elemen — pembanding, bukan tampilan. */
  const terpasang = useRef<string | null>(null);
  const [ukuran, setUkuran] = useState<{ w: number; h: number } | null>(null);

  const seg = useMemo(() => segmenPada(segments, posMs), [segments, posMs]);

  /* Pasang berkas yang benar, lalu kejar jamnya. */
  useEffect(() => {
    const el = vidRef.current;
    if (!el) return;

    if (!seg) {
      // Di bolong: lepaskan berkasnya supaya yang terlihat memang kekosongan,
      // bukan frame terakhir yang membeku dan terbaca sebagai rekaman.
      if (terpasang.current !== null) {
        terpasang.current = null;
        el.removeAttribute('src');
        el.load();
      }
      return;
    }

    const mulai = new Date(seg.startedAt).getTime();
    const target = (posMs - mulai) / 1000;

    if (terpasang.current !== seg.id) {
      terpasang.current = seg.id;
      el.src = `/api/v1/recordings/${seg.id}/play`;
      // `currentTime` sebelum metadata siap diabaikan browser, jadi ia dipasang
      // di `loadedmetadata` — sekali, lalu dilepas.
      const pasang = () => {
        el.currentTime = Math.max(0, target);
        if (main) void el.play().catch(() => {});
      };
      el.addEventListener('loadedmetadata', pasang, { once: true });
      return;
    }

    // Berkasnya sudah benar: koreksi hanyutan saja. Tanpa ambang, tiap frame
    // akan memicu `currentTime` baru dan pemutarannya justru tersendat.
    // Jangan seek lagi selagi seek sebelumnya atau buffering belum selesai: tiap
    // `currentTime` baru membatalkan permintaan range yang sedang jalan, dan di
    // 10 Hz itu jadi ribuan request per segmen sampai 429 (audit 13 Sep 2026).
    if (
      Number.isFinite(el.duration) &&
      !el.seeking &&
      el.readyState >= 2 &&
      Math.abs(el.currentTime - target) > AMBANG_KEJAR_S
    ) {
      el.currentTime = Math.max(0, target);
    }
  }, [seg, posMs, main]);

  /* Jalan / berhenti mengikuti jam induk. */
  useEffect(() => {
    const el = vidRef.current;
    if (!el || terpasang.current === null) return;
    el.playbackRate = Math.min(rate, 8);
    if (main) void el.play().catch(() => {});
    else el.pause();
  }, [main, rate, seg]);

  return (
    <div className="relative min-h-0 overflow-hidden rounded-lg bg-black">
      <video
        ref={vidRef}
        muted
        playsInline
        preload="auto"
        onLoadedMetadata={(e) =>
          setUkuran({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })
        }
        className="absolute inset-0 h-full w-full object-contain"
      />

      {!seg && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="rounded-md bg-black/60 px-3 py-1.5 text-sm text-white/80">
            {t('No recording at this moment')}
          </p>
        </div>
      )}

      <div className="pointer-events-none absolute left-2.5 top-2 max-w-[70%] drop-shadow">
        <p className="truncate text-sm font-semibold text-white">{kamera.name}</p>
        <p className="truncate text-[11px] tabular-nums text-white/70">
          {ukuran && `${ukuran.w} × ${ukuran.h}`}
          {ukuran && ' · '}
          {t('{n} fps', { n: kamera.streamFps })}
        </p>
      </div>

      <button
        type="button"
        onClick={onLepas}
        title={t('Remove from view')}
        aria-label={t('Remove {nama} from this view', { nama: kamera.name })}
        className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-md bg-black/50 text-white ring-1 ring-white/20 hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
    </div>
  );
}

/* ── Halaman ──────────────────────────────────────────────────────────────── */

export function MultiVideosPage() {
  const { t } = useBahasa();

  const [cameras, setCameras] = useState<Camera[] | null>(null);
  const [dipilih, setDipilih] = useState<string[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(KUNCI_SIMPAN) ?? '[]') as unknown;
      return Array.isArray(v) ? (v as string[]).slice(0, MAKS_KAMERA) : [];
    } catch {
      return [];
    }
  });
  const [timelines, setTimelines] = useState<Record<string, Timeline>>({});
  const [tanggal, setTanggal] = useState(() => toDateInput(new Date()));
  const [rentang, setRentang] = useState<IdRentang>('1H');
  const [memuat, setMemuat] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bukaPilih, setBukaPilih] = useState(false);
  const [cari, setCari] = useState('');
  const kotakPilihRef = useRef<HTMLDivElement>(null);

  /**
   * Klik di mana pun di luar daftar kamera menutupnya.
   *
   * Dilaporkan lapangan 8 Sep 2026: *"kalo selesai pilih atau tambah kamera
   * 'klik dimanapun' ga bikin ilang dropdownnya, harus klik si dropdown 'add
   * camera' baru ilang. nyebelin."* — benar, dan pola ini sudah dipakai di
   * pemilih kamera `/videos` maupun di panel "Lainnya"; yang di sini memang
   * terlewat.
   *
   * `pointerdown` dan bukan `click`: ia menutup pada sentuhan pertama, sebelum
   * jari sempat terangkat, jadi tidak ada sekejap ketika daftarnya masih
   * menutupi apa yang barusan ditunjuk orangnya.
   */
  useEffect(() => {
    if (!bukaPilih) return;
    const onLuar = (e: PointerEvent) => {
      if (!kotakPilihRef.current?.contains(e.target as Node)) setBukaPilih(false);
    };
    document.addEventListener('pointerdown', onLuar);
    return () => document.removeEventListener('pointerdown', onLuar);
  }, [bukaPilih]);

  const [posMs, setPosMs] = useState<number | null>(null);
  const [main, setMain] = useState(false);
  const [rate, setRate] = useState(1);
  /**
   * Mundur cepat, `0` = tidak sedang mundur.
   *
   * Dipisah dari `rate` dengan alasan yang sama seperti di `/videos`: tidak ada
   * browser yang menerima `playbackRate` negatif, jadi mundur bukan "laju lain"
   * melainkan **cara jalan yang lain**. Di sini bahkan lebih murah daripada di
   * sana — jamnya sudah berdiri sendiri, jadi mundur cukup berarti jam yang
   * dikurangi, dan empat elemen video yang tetap diam sambil dilempar ke posisi
   * baru sepuluh kali sedetik.
   */
  const [mundur, setMundur] = useState(0);
  const [bantuan, setBantuan] = useState(false);
  const layarLebar = useLayarLebar();

  const akhir = useMemo(() => ujungHari(tanggal), [tanggal]);
  const jam = RENTANG_CEPAT.find((r) => r.id === rentang)?.jam ?? 1;
  const fromMs = akhir - jam * 3600_000;
  const toMs = akhir;

  useEffect(() => {
    void api<{ cameras: Camera[] }>('/cameras')
      .then((r) => setCameras(r.cameras))
      .catch((e) => setError((e as Error).message));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(KUNCI_SIMPAN, JSON.stringify(dipilih));
    } catch {
      // Mode privat — pilihannya cukup berlaku sesi ini.
    }
  }, [dipilih]);

  /**
   * Timeline tiap kamera ditarik **paralel**, dan yang gagal tidak menjatuhkan
   * yang lain: satu kamera yang baru dihapus orang tidak boleh membuat tiga
   * sudut lainnya ikut kosong.
   */
  const muat = useCallback(async () => {
    // Layar sempit tidak pernah menampilkan grid ini, jadi ia juga tidak perlu
    // menarik empat timeline sekaligus lewat sambungan seluler.
    if (!layarLebar || dipilih.length === 0) {
      setTimelines({});
      return;
    }
    setMemuat(true);
    try {
      const hasil = await Promise.all(
        dipilih.map(async (id) => {
          const q = new URLSearchParams({
            camera_id: id,
            from: new Date(fromMs).toISOString(),
            to: new Date(toMs).toISOString(),
          });
          try {
            return [id, await api<Timeline>(`/recordings/timeline?${q}`)] as const;
          } catch {
            return [id, null] as const;
          }
        }),
      );
      const peta: Record<string, Timeline> = {};
      for (const [id, tl] of hasil) if (tl) peta[id] = tl;
      setTimelines(peta);
      setError(null);

      // Playhead mendarat di rekaman paling awal yang benar-benar ada, bukan di
      // ujung kiri rentang: rentang 6 jam yang tiga jam pertamanya kosong akan
      // membuka halaman ini pada empat kotak hitam.
      const mulaiAwal = Object.values(peta)
        .flatMap((tl) => tl.segments.map((s) => new Date(s.startedAt).getTime()))
        .sort((a, b) => a - b)[0];
      setPosMs(mulaiAwal ?? fromMs);
      setMain(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setMemuat(false);
    }
  }, [dipilih, fromMs, toMs, layarLebar]);

  useEffect(() => {
    void muat();
  }, [muat]);

  /**
   * Jam induk.
   *
   * `requestAnimationFrame` dan bukan `setInterval`: yang dikejar kehalusan
   * playhead, dan rAF berhenti sendiri saat tab disembunyikan — persis yang
   * diinginkan, karena empat video yang tetap "berjalan" di tab tersembunyi
   * cuma memanaskan mesin tanpa ada yang melihatnya.
   */
  const rafRef = useRef<number | null>(null);
  const jamRef = useRef<number>(0);
  const terakhirRef = useRef<number>(0);
  useEffect(() => {
    if ((!main && mundur === 0) || posMs === null) return;
    // Satu jam, dua arah. Maju memakai `rate` dan elemen videonya ikut memutar;
    // mundur memakai `-mundur` dan elemennya tetap diam — yang bergerak cuma
    // posisi yang dikejarnya (lihat `Ubin`).
    const laju = main ? rate : -mundur;
    jamRef.current = performance.now();
    terakhirRef.current = jamRef.current;
    const langkah = (kini: number) => {
      rafRef.current = requestAnimationFrame(langkah);

      /**
       * Jamnya maju tiap frame, tapi **state-nya** hanya sepuluh kali per detik.
       *
       * `setPosMs` di tiap frame berarti empat ubin, empat pita, dan playhead
       * dirender ulang enam puluh kali per detik — di browser pos satpam yang
       * sudah men-dekode empat aliran 1080p, itu justru yang membuat gambarnya
       * tersendat. Pada sumbu enam jam satu piksel ≈ 20 detik, jadi 10 Hz sudah
       * jauh lebih halus daripada yang bisa dilihat mata di layar itu.
       *
       * Yang dibaca tetap selisih jam dinding sungguhan (`lewat`), bukan
       * kelipatan tetap — jadi frame yang terlambat tidak membuat jamnya
       * tertinggal.
       */
      if (kini - terakhirRef.current < 100) return;
      const lewat = kini - jamRef.current;
      jamRef.current = kini;
      terakhirRef.current = kini;
      setPosMs((p) => {
        if (p === null) return p;
        const berikut = p + lewat * laju;
        // Berhenti di ujung rentang, bukan berjalan ke masa depan yang kosong.
        if (berikut >= toMs) {
          setMain(false);
          return toMs;
        }
        // Ujung kiri sama tegasnya: mundur yang menembus awal rentang berarti
        // jam yang berjalan di tempat yang timeline-nya tidak pernah dimuat.
        if (berikut <= fromMs) {
          setMundur(0);
          return fromMs;
        }
        return berikut;
      });
    };
    rafRef.current = requestAnimationFrame(langkah);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // `posMs` sengaja tidak jadi dependensi: ia berubah tiap frame, dan
    // memasukkannya akan membongkar-pasang rAF enam puluh kali per detik.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [main, mundur, rate, fromMs, toMs]);

  const kameraDipilih = useMemo(
    () => dipilih.map((id) => cameras?.find((c) => c.id === id)).filter((c): c is Camera => !!c),
    [dipilih, cameras],
  );

  const hasilCari = useMemo(
    () => (cameras ?? []).filter((c) => cocokKamera(c, cari.trim())).slice(0, 60),
    [cameras, cari],
  );

  /** Geser jam sekian detik — tombol −10s/−1s/+1s/+10s dan panah papan ketik. */
  const geser = useCallback(
    (detik: number) => {
      setPosMs((p) => (p === null ? p : Math.max(fromMs, Math.min(toMs, p + detik * 1000))));
    },
    [fromMs, toMs],
  );

  /* ── Transport: maju/mundur bertingkat ──────────────────────────────────── */

  const putar = useCallback(() => {
    setMundur(0);
    setMain(true);
  }, []);

  /**
   * Jeda — sekaligus pulang ke 1×, persis seperti `/videos`.
   *
   * Alasannya sama dan sudah dibayar mahal sekali di sana: laju cepat itu
   * keadaan sementara. Orang menekan ⏩ untuk menyusul sesuatu lalu menjeda
   * begitu ketemu, dan melanjutkan dengan 8× yang masih menempel berarti empat
   * gambar yang langsung kabur lagi dari yang barusan dicari.
   */
  const jeda = useCallback(() => {
    setMain(false);
    setMundur(0);
    setRate(1);
  }, []);

  /** Maju lebih cepat — ⏩ dan `L`. Menekan berulang menaiki `LAJU`. */
  const majuLebihCepat = useCallback(() => {
    setMundur(0);
    setRate((r) => LAJU[Math.min(LAJU.indexOf(r) + 1, LAJU.length - 1)] ?? 1);
    setMain(true);
  }, []);

  /** Mundur lebih cepat — ⏪ dan `J`. Elemen videonya justru harus diam. */
  const mundurLebihCepat = useCallback(() => {
    setRate(1);
    setMain(false);
    setMundur((m) => (m === 0 ? 1 : LAJU[Math.min(LAJU.indexOf(m) + 1, LAJU.length - 1)] ?? 8));
  }, []);

  /**
   * Kembali ke 1× **dan tetap berjalan** — tombol "1×".
   *
   * Bukan hiasan: tanpa ini satu-satunya jalan keluar dari 8× atau dari mundur
   * adalah menjeda dulu, dan itu keluhan yang sudah dilaporkan sebagai
   * *critical* untuk halaman satu kamera 24 Agu 2026. Halaman ini mewarisi
   * bentuk yang sama supaya keduanya tidak perlu dipelajari dua kali.
   */
  const lajuNormal = useCallback(() => {
    setRate(1);
    setMundur(0);
    setMain(true);
  }, []);

  /* ── Jendela yang terlihat: zoom & geser sumbu ──────────────────────────── */

  /**
   * Jendela yang sedang terlihat. `null` = belum ada rentang yang dimuat.
   *
   * Sampai 8 Sep 2026 sumbu halaman ini **selalu** persis selebar rentang yang
   * dimuat: memilih 24H berarti seluruh hari dipadatkan ke satu sumbu, satu
   * piksel ≈ 80 detik, dan tidak ada satu pun cara mendekat selain mengganti
   * rentangnya — yang berarti memuat ulang empat timeline hanya untuk melihat
   * lebih dekat. Sekarang rentang muat dan jendela lihat dua hal yang berbeda,
   * seperti di `/videos`.
   */
  const [view, setView] = useState<{ from: number; to: number } | null>(null);

  // Rentang muat berganti (tanggal atau rentang cepat) → jendela pulang ke
  // seluruh rentang baru itu. Mempertahankan jendela lama berarti mendarat di
  // jam yang datanya memang tidak ikut ditarik.
  useEffect(() => {
    setView({ from: fromMs, to: toMs });
  }, [fromMs, toMs]);

  const vFrom = view?.from ?? fromMs;
  const vTo = view?.to ?? toMs;
  const rentangView = Math.max(1, vTo - vFrom);

  const persen = useCallback(
    (ms: number) => ((ms - vFrom) / rentangView) * 100,
    [vFrom, rentangView],
  );

  /**
   * Sumbu waktunya, **tanpa kolom nama**.
   *
   * Ini ref yang menentukan seluruh pemetaan piksel ↔ waktu di halaman ini.
   * Sebelumnya yang diukur wadah luarnya, yang ikut memuat kolom nama kamera
   * selebar `w-40`: klik di pita mendarat ±160px lebih ke kiri daripada yang
   * ditunjuk, sementara playhead digambar dengan offset yang benar. Dua
   * kebenaran yang berbeda tentang satu sumbu adalah cara tercepat membuat
   * orang berhenti mempercayai timeline.
   */
  const sumbuRef = useRef<HTMLDivElement>(null);

  const msDari = useCallback(
    (clientX: number) => {
      const r = sumbuRef.current?.getBoundingClientRect();
      if (!r) return vFrom;
      const rasio = Math.max(0, Math.min(1, (clientX - r.left) / Math.max(1, r.width)));
      return vFrom + rasio * rentangView;
    },
    [vFrom, rentangView],
  );

  /** Zoom di sekitar satu titik supaya yang sedang dilihat tidak lari. */
  const zoomKe = useCallback(
    (faktor: number, pusatMs: number | null) => {
      setView((v) => {
        const cur = v ?? { from: fromMs, to: toMs };
        const rentang = Math.max(1, cur.to - cur.from);
        const penuh = Math.max(1, toMs - fromMs);
        const pusat =
          pusatMs !== null && pusatMs >= cur.from && pusatMs <= cur.to
            ? pusatMs
            : (cur.from + cur.to) / 2;
        const baru = Math.max(JENDELA_MIN_MS, Math.min(penuh, rentang * faktor));
        let from = pusat - (pusat - cur.from) * (baru / rentang);
        let to = from + baru;
        if (from < fromMs) {
          from = fromMs;
          to = from + baru;
        }
        if (to > toMs) {
          to = toMs;
          from = to - baru;
        }
        return { from, to };
      });
    },
    [fromMs, toMs],
  );

  /**
   * Zoom lewat tombol −/+, berputar di sekitar **playhead**.
   *
   * Yang sedang diperiksa orang posisi itu; zoom yang memusat di tengah jendela
   * akan membuangnya ke luar layar pada tarikan kedua.
   */
  const zoomSumbu = useCallback((faktor: number) => zoomKe(faktor, posMs), [zoomKe, posMs]);

  /* ── Gestur di atas pita: geser, cubit, klik ─────────────────────────────── */

  const pitaRef = useRef<HTMLDivElement>(null);
  /** Seretan yang sedang berjalan. Ref, bukan state: dibaca puluhan kali/detik. */
  const geserRef = useRef<{ x: number; viewAwal: number; rentangAwal: number; lebar: number } | null>(
    null,
  );
  const jariRef = useRef(new Map<number, number>());
  const cubitRef = useRef<{ jarak: number; pusatMs: number; rentangAwal: number } | null>(null);

  function pitaDown(e: React.PointerEvent) {
    const r = sumbuRef.current?.getBoundingClientRect();
    if (!r) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    jariRef.current.set(e.pointerId, e.clientX);

    // Jari kedua mendarat: yang berjalan bukan geseran lagi, melainkan cubitan.
    if (jariRef.current.size === 2) {
      const [x1, x2] = [...jariRef.current.values()];
      const kiri = Math.min(x1!, x2!);
      const kanan = Math.max(x1!, x2!);
      geserRef.current = null;
      cubitRef.current = {
        jarak: Math.max(1, kanan - kiri),
        pusatMs: msDari((kiri + kanan) / 2),
        rentangAwal: rentangView,
      };
      return;
    }

    geserRef.current = { x: e.clientX, viewAwal: vFrom, rentangAwal: rentangView, lebar: Math.max(1, r.width) };
  }

  function pitaMove(e: React.PointerEvent) {
    if (jariRef.current.has(e.pointerId)) jariRef.current.set(e.pointerId, e.clientX);

    const c = cubitRef.current;
    if (c && jariRef.current.size >= 2) {
      const [x1, x2] = [...jariRef.current.values()];
      const jarak = Math.max(1, Math.abs(x1! - x2!));
      zoomKe((c.rentangAwal * (c.jarak / jarak)) / rentangView, c.pusatMs);
      return;
    }

    const g = geserRef.current;
    if (!g) return;
    const deltaMs = ((g.x - e.clientX) / g.lebar) * g.rentangAwal;
    const r = Math.min(g.rentangAwal, Math.max(1, toMs - fromMs));
    let f = g.viewAwal + deltaMs;
    f = Math.min(Math.max(f, fromMs), Math.max(fromMs, toMs - r));
    setView({ from: f, to: f + r });
  }

  function pitaUp(e: React.PointerEvent) {
    const g = geserRef.current;
    geserRef.current = null;
    jariRef.current.delete(e.pointerId);
    if (jariRef.current.size < 2) cubitRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (!g) return;
    // Seretan yang lebih pendek dari ambang ini bukan seretan — itu klik yang
    // tangannya sedikit bergoyang, dan klik di pita berarti "ke sini".
    if (Math.abs(e.clientX - g.x) > GESER_MINIMAL_PX) return;
    setPosMs(Math.max(fromMs, Math.min(toMs, msDari(e.clientX))));
  }

  // `passive: false` wajib: handler ini memanggil `preventDefault()` supaya roda
  // men-zoom sumbu, bukan menggulung halaman. React memasang listener wheel
  // sebagai passive, jadi ia tidak bisa lewat prop `onWheel`.
  useEffect(() => {
    const el = pitaRef.current;
    if (!el) return;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      // Penakar yang sama dengan zoom gambar dan timeline `/videos`, jadi roda
      // terasa sama di ketiganya. Dibalik karena `faktor` di sini mengalikan
      // rentang waktu: faktor kecil = jendela menyempit = zoom masuk.
      zoomKe(1 / faktorRodaZoom(ev), msDari(ev.clientX));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomKe, msDari]);

  /* ── Papan ketik ────────────────────────────────────────────────────────── */

  /**
   * Peta tombol yang sama dengan `/videos` sejauh yang punya arti di sini.
   *
   * Yang tidak ikut: `I`/`O` (mark in/out), `M`/`N` (bookmark), dan `F` (layar
   * penuh) — ketiganya milik halaman satu kamera, dan halaman ini memang tidak
   * punya benda yang mereka tunjuk. Yang ikut adalah seluruh cara menyusuri
   * waktu, karena di situlah dua halaman ini melakukan hal yang persis sama dan
   * tidak boleh menuntut dua kebiasaan yang berbeda.
   *
   * Panahnya melangkah 1 detik / 10 detik, bukan satu bingkai seperti di
   * `/videos`: di sana ada satu elemen yang bisa dimajukan tepat satu bingkai,
   * di sini ada empat elemen yang mengejar jam bersama dan langkah sekecil itu
   * tenggelam di dalam ambang kejar 0,75 detik.
   */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (dipilih.length === 0 || posMs === null) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          main || mundur ? jeda() : putar();
          return;
        case 'l':
        case 'L':
          e.preventDefault();
          majuLebihCepat();
          return;
        case 'j':
        case 'J':
          e.preventDefault();
          mundurLebihCepat();
          return;
        case 'k':
        case 'K':
          e.preventDefault();
          jeda();
          return;
        case 'ArrowRight':
          e.preventDefault();
          geser(e.shiftKey ? 10 : 1);
          return;
        case 'ArrowLeft':
          e.preventDefault();
          geser(e.shiftKey ? -10 : -1);
          return;
        case '+':
        case '=':
          e.preventDefault();
          zoomSumbu(0.6);
          return;
        case '-':
        case '_':
          e.preventDefault();
          zoomSumbu(1.7);
          return;
        /**
         * Home / End relatif jendela (§G10.11 poin 6) — aturan yang sama persis
         * dengan `/videos`, dan itu memang seluruh alasannya ada di sini:
         * tombol yang berarti dua hal berbeda di dua halaman yang terlihat
         * sama adalah tombol yang tidak dipakai di kedua-duanya.
         *
         * Lebih sederhana daripada di sana karena halaman ini tidak menambatkan
         * playhead ke segmen mana pun — jamnya berdiri sendiri, jadi tepi
         * jendela selalu bisa dituju apa adanya, termasuk kalau ia jatuh di
         * tengah bolong rekaman.
         */
        case 'Home':
        case 'End': {
          e.preventDefault();
          const keKiri = e.key === 'Home';
          const tepi = keKiri ? vFrom : vTo;
          const toleransi = Math.max(500, rentangView * 0.001);
          const sudahDiTepi = keKiri ? posMs <= tepi + toleransi : posMs >= tepi - toleransi;
          if (!sudahDiTepi) {
            setPosMs(Math.max(fromMs, Math.min(toMs, tepi)));
            return;
          }
          let from = keKiri ? vFrom - rentangView : vFrom + rentangView;
          from = Math.min(Math.max(from, fromMs), Math.max(fromMs, toMs - rentangView));
          setView({ from, to: from + rentangView });
          setPosMs(keKiri ? from : from + rentangView);
          return;
        }
        case '?':
          setBantuan((v) => !v);
          return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dipilih.length, posMs, main, mundur, jeda, putar, majuLebihCepat, mundurLebihCepat, geser, zoomSumbu, vFrom, vTo, rentangView, fromMs, toMs]);

  /**
   * Layar sempit: halaman ini tidak dibuka, dan alasannya disebut.
   *
   * Bukan halaman kosong dan bukan pengalihan diam-diam ke `/videos`: yang
   * sampai ke sini datang dari tautan lama, dari bookmark, atau dari layar yang
   * baru saja diputar tegak — ketiganya perlu tahu bahwa yang dicarinya ada,
   * cuma tidak di lebar ini. Pengalihan otomatis membuat tombol "kembali"
   * memantulkan orang bolak-balik.
   */
  if (!layarLebar) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 p-6 text-center">
        <svg viewBox="0 0 24 24" className="h-10 w-10 text-muted" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="4" width="20" height="14" rx="2" />
          <path d="M8 20h8M12 18v2" />
        </svg>
        <h1 className="text-xl font-semibold">{t('Multi-Camera Playback')}</h1>
        <p className="max-w-sm text-sm text-muted">
          {t('Four angles at once need a wide screen. On a phone each camera would be barely taller than its own label — open this on a desktop or a tablet in landscape.')}
        </p>
        <Link
          to="/videos"
          className="flex min-h-tap items-center rounded-md bg-accent px-4 text-sm font-medium text-accent-contrast"
        >
          {t('Open single-camera playback')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-x-clip p-3">
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="text-xl font-semibold">{t('Multi-Camera Playback')}</h1>
        <p className="text-sm text-muted">
          {t('One timeline. Several angles. The same second, seen from every side.')}
        </p>
        <Link
          to="/videos"
          className="ml-auto flex min-h-tap items-center rounded-md border border-line px-3 text-sm hover:bg-subtle"
        >
          {t('Single camera')}
        </Link>
      </div>

      {/* ── Filter ─────────────────────────────────────────────────────────── */}
      <div className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-line bg-surface p-3">
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="tanggal-multi">{t('Date')}</label>
          <input
            id="tanggal-multi"
            type="date"
            value={tanggal}
            max={toDateInput(new Date())}
            min={toDateInput(new Date(Date.now() - RETENSI_MAX_HARI * 86400_000))}
            onChange={(e) => setTanggal(e.target.value)}
            className="min-h-tap rounded-md border border-line bg-surface px-2"
          />
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <span id="label-rentang-multi">{t('Quick range')}</span>
          <div className="flex items-center gap-1" role="group" aria-labelledby="label-rentang-multi">
            {RENTANG_CEPAT.map((r) => (
              <button
                key={r.id}
                type="button"
                aria-pressed={rentang === r.id}
                onClick={() => setRentang(r.id)}
                className={`min-h-tap min-w-11 rounded-md px-3 text-sm font-medium tabular-nums ${
                  rentang === r.id
                    ? 'bg-accent text-accent-contrast'
                    : 'border border-line hover:bg-subtle'
                }`}
              >
                {r.id}
              </button>
            ))}
          </div>
        </div>

        <span className="self-end pb-2.5 text-xs text-muted">WIB</span>

        <div ref={kotakPilihRef} className="relative ml-auto self-end">
          <button
            type="button"
            onClick={() => setBukaPilih((v) => !v)}
            aria-expanded={bukaPilih}
            className="flex min-h-tap items-center gap-1.5 rounded-md border border-line px-3 text-sm hover:bg-subtle"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {t('Add cameras')} ({dipilih.length}/{MAKS_KAMERA})
          </button>

          {bukaPilih && (
            <div className="absolute right-0 top-full z-30 mt-1 w-80 max-w-[85vw] rounded-md border border-line bg-surface p-2 shadow-lg">
              <input
                autoFocus
                value={cari}
                onChange={(e) => setCari(e.target.value)}
                placeholder={t('Search Name, Host or Path')}
                className="mb-2 min-h-tap w-full rounded-md border border-line bg-page px-2 text-sm"
              />
              <ul className="max-h-72 overflow-y-auto">
                {hasilCari.map((c) => {
                  const ada = dipilih.includes(c.id);
                  const penuh = dipilih.length >= MAKS_KAMERA;
                  return (
                    <li key={c.id}>
                      <label
                        className={`flex min-h-tap items-center gap-2 px-2 text-sm ${
                          !ada && penuh ? 'opacity-40' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={ada}
                          disabled={!ada && penuh}
                          onChange={() =>
                            setDipilih((d) =>
                              d.includes(c.id)
                                ? d.filter((x) => x !== c.id)
                                : d.length < MAKS_KAMERA
                                  ? [...d, c.id]
                                  : d,
                            )
                          }
                        />
                        <span className="truncate">{c.name}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              {dipilih.length >= MAKS_KAMERA && (
                <p className="mt-2 px-2 text-xs text-muted">
                  {t('Four cameras at a time — remove one to add another.')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="shrink-0 text-sm text-amber-600" role="alert">
          {error}
        </p>
      )}

      {/* ── Grid ───────────────────────────────────────────────────────────── */}
      {kameraDipilih.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-dashed border-line p-10 text-center">
          <div>
            <p className="text-lg">{t('Pick up to four cameras to compare')}</p>
            <p className="mt-1 text-sm text-muted">
              {t('They all follow one clock, so the same second lines up across every angle.')}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
          {kameraDipilih.map((c) => (
            <Ubin
              key={c.id}
              kamera={c}
              segments={timelines[c.id]?.segments ?? []}
              posMs={posMs ?? fromMs}
              main={main}
              rate={rate}
              onLepas={() => setDipilih((d) => d.filter((x) => x !== c.id))}
            />
          ))}
        </div>
      )}

      {/* ── Timeline induk + pita per kamera ───────────────────────────────── */}
      {kameraDipilih.length > 0 && (
        <div className="shrink-0 rounded-lg border border-line bg-surface p-3">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <strong className="text-lg tabular-nums">
              {posMs === null ? '—' : formatWibJam(posMs)}
            </strong>
            <span className="text-sm text-muted">
              {posMs === null ? '' : formatWibTanggal(posMs)}
            </span>
            {memuat && <span className="text-xs text-muted">{t('Loading…')}</span>}
          </div>

          {/* Satu sumbu untuk semua. Pita per kamera di bawahnya memakai
              koordinat yang sama persis, jadi kolom yang sejajar benar-benar
              detik yang sama — itu seluruh janji halaman ini. */}
          <div
            ref={pitaRef}
            className="relative mt-2 touch-none cursor-grab select-none active:cursor-grabbing"
            onPointerDown={pitaDown}
            onPointerMove={pitaMove}
            onPointerUp={pitaUp}
            onPointerCancel={pitaUp}
          >
            {/* Penggaris waktu.

                Dilaporkan lapangan 8 Sep 2026: *"di atas timelinenya gak ada
                timerullernya, jadi bingung, jadi gak bisa nentuin titik
                videohead"*. Benar — tanpa angka, pita itu cuma balok biru dan
                satu-satunya cara menemukan jam tertentu adalah menebak lalu
                mengoreksi.

                Lebar kolom namanya (`w-40`) dan jaraknya (`gap-2`) diulang di
                sini persis: penggaris yang bergeser satu kolom dari pitanya
                lebih buruk daripada tidak ada penggaris sama sekali, karena ia
                terbaca sebagai kebenaran. */}
            <div className="mb-1 flex items-end gap-2">
              <span className="w-40 shrink-0" aria-hidden="true" />
              <div ref={sumbuRef} className="relative h-4 flex-1">
                {TIK.map((f) => {
                  const ms = vFrom + rentangView * f;
                  return (
                    <span
                      key={f}
                      className="absolute bottom-0 flex -translate-x-1/2 flex-col items-center"
                      style={{ left: `${f * 100}%` }}
                    >
                      <span className="text-[10px] tabular-nums leading-none text-muted">
                        {formatWibJam(ms)}
                      </span>
                      <span className="mt-0.5 h-1 w-px bg-line" aria-hidden="true" />
                    </span>
                  );
                })}
              </div>
            </div>

            <ul className="space-y-1.5">
              {kameraDipilih.map((c) => {
                const tl = timelines[c.id];
                return (
                  <li key={c.id} className="flex items-center gap-2">
                    <span className="w-40 shrink-0 truncate text-xs" title={c.name}>
                      {c.name}
                    </span>
                    <span className="relative h-4 flex-1 overflow-hidden rounded bg-line">
                      {(tl?.segments ?? []).map((sg) => {
                        const a = new Date(sg.startedAt).getTime();
                        const b = a + sg.durationS * 1000;
                        return (
                          <span
                            key={sg.id}
                            className="absolute inset-y-0 bg-accent"
                            style={{
                              left: `${persen(a)}%`,
                              width: `${Math.max(0.2, persen(b) - persen(a))}%`,
                            }}
                          />
                        );
                      })}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Playhead menembus seluruh pita — satu garis, satu detik. */}
            {posMs !== null && posMs >= vFrom && posMs <= vTo && (
              <span
                className="pointer-events-none absolute inset-y-0 w-0.5 bg-red-500"
                style={{ left: `calc(10rem + 0.5rem + (100% - 10rem - 0.5rem) * ${persen(posMs) / 100})` }}
                aria-hidden="true"
              />
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-accent" aria-hidden="true" />
              {t('Recording')}
            </span>
            <span className="flex items-center gap-1.5 text-muted">
              <span className="h-2.5 w-2.5 rounded-full bg-line" aria-hidden="true" />
              {t('No recording')}
            </span>
            {/* Jam yang disebut di sini **jendela yang terlihat**, bukan
                rentang yang dimuat: begitu sumbunya bisa di-zoom, angka yang
                tidak ikut berubah saat gambarnya berubah terbaca sebagai
                timeline yang macet. */}
            <span className="tabular-nums text-muted">
              {formatWibJam(vFrom)} – {formatWibJam(vTo)}
            </span>

            <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
              <KendaliSkala
                rentangMs={rentangView}
                penuhMs={Math.max(1, toMs - fromMs)}
                lantaiMs={JENDELA_MIN_MS}
                onRentang={(ms) => zoomKe(ms / rentangView, posMs)}
                onMuatPenuh={() => setView({ from: fromMs, to: toMs })}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Transport ──────────────────────────────────────────────────────── */}
      {kameraDipilih.length > 0 && (
        <div className="flex shrink-0 flex-wrap items-center justify-center gap-2">
          {/* ⏪ dan ⏩ mengapit kelompok geser-detik, bukan berdiri di ujung
              baris: keduanya menjawab pertanyaan yang sama dengan −10s/+10s
              ("lebih ke sana"), cuma dengan satuan yang berbeda, dan tangan
              yang mencari salah satunya hampir selalu barusan menekan yang
              lain. */}
          <button
            type="button"
            onClick={mundurLebihCepat}
            title={mundur > 0 ? t('Rewinding {n}×', { n: mundur }) : t('Rewind (J)')}
            aria-label={mundur > 0 ? t('Rewinding {n}×', { n: mundur }) : t('Rewind')}
            aria-pressed={mundur > 0}
            className={`flex min-h-tap items-center gap-1 rounded-md border px-3 text-sm tabular-nums ${
              mundur > 0 ? 'border-accent bg-accent text-accent-contrast' : 'border-line hover:bg-subtle'
            }`}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M11 6.2a.8.8 0 0 1 1.3-.6v12.8a.8.8 0 0 1-1.3-.6zM3 6.2a.8.8 0 0 1 1.3-.6l8 5.8a.8.8 0 0 1 0 1.2l-8 5.8a.8.8 0 0 1-1.3-.6z" />
            </svg>
            {mundur > 0 && `${mundur}×`}
          </button>

          {[-10, -1].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => geser(d)}
              className="min-h-tap rounded-md border border-line px-3 text-sm tabular-nums hover:bg-subtle"
            >
              {d}s
            </button>
          ))}

          <button
            type="button"
            onClick={() => (main || mundur ? jeda() : putar())}
            title={main || mundur ? t('Pause (Space)') : t('Play (Space)')}
            aria-label={main || mundur ? t('Pause') : t('Play')}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.94]"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor" aria-hidden="true">
              {main || mundur ? (
                <>
                  <rect x="6.5" y="5" width="3.6" height="14" rx="1" />
                  <rect x="13.9" y="5" width="3.6" height="14" rx="1" />
                </>
              ) : (
                <path d="M9 5.14a1 1 0 0 1 1.53-.85l9.72 6.86a1 1 0 0 1 0 1.7l-9.72 6.86A1 1 0 0 1 9 18.86z" />
              )}
            </svg>
          </button>

          {[1, 10].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => geser(d)}
              className="min-h-tap rounded-md border border-line px-3 text-sm tabular-nums hover:bg-subtle"
            >
              +{d}s
            </button>
          ))}

          <button
            type="button"
            onClick={majuLebihCepat}
            title={t('Fast forward (L)')}
            aria-label={t('Fast forward')}
            aria-pressed={main && rate > 1}
            className={`flex min-h-tap items-center gap-1 rounded-md border px-3 text-sm tabular-nums ${
              main && rate > 1
                ? 'border-accent bg-accent text-accent-contrast'
                : 'border-line hover:bg-subtle'
            }`}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M13 6.2a.8.8 0 0 0-1.3-.6v12.8a.8.8 0 0 0 1.3-.6zM21 6.2a.8.8 0 0 0-1.3-.6l-8 5.8a.8.8 0 0 0 0 1.2l8 5.8a.8.8 0 0 0 1.3-.6z" />
            </svg>
            {main && rate > 1 && `${rate}×`}
          </button>

          {/* Pintu keluar dari laju cepat yang tidak lewat papan ketik (§G10.1).
              Padam saat memang sudah 1× maju — tombol yang tidak mengubah apa
              pun cuma menambah satu benda untuk diperiksa mata. */}
          {(mundur > 0 || rate > 1) && (
            <button
              type="button"
              onClick={lajuNormal}
              className="min-h-tap rounded-md border border-line px-3 text-sm font-semibold tabular-nums hover:bg-subtle"
            >
              1×
            </button>
          )}

          <label className="ml-2 flex items-center gap-1.5 text-sm">
            <span className="sr-only">{t('Playback speed')}</span>
            {/* Satu penunjuk laju untuk dua arah. Nilai negatif = mundur, dan
                itu sengaja: dua kotak terpisah ("laju" dan "arah") berarti dua
                tempat yang bisa saling bertentangan di layar. */}
            <select
              value={mundur > 0 ? `-${mundur}` : String(rate)}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v < 0) {
                  setRate(1);
                  setMain(false);
                  setMundur(-v);
                } else {
                  setMundur(0);
                  setRate(v);
                }
              }}
              className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm font-semibold tabular-nums"
            >
              {[...LAJU].reverse().map((v) => (
                <option key={`-${v}`} value={`-${v}`}>
                  −{v}×
                </option>
              ))}
              {LAJU.map((v) => (
                <option key={v} value={v}>
                  {v}×
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setBantuan((v) => !v)}
            aria-expanded={bantuan}
            aria-controls="peta-tombol-multi"
            className={`flex min-h-tap items-center gap-1.5 rounded-md border px-3 text-sm ${
              bantuan ? 'border-accent text-accent-text' : 'border-line hover:bg-subtle'
            }`}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.5 9.5a2.5 2.5 0 1 1 3 2.45V14" />
              <path d="M12 17h.01" />
            </svg>
            {t('Help (?)')}
          </button>
        </div>
      )}

      {kameraDipilih.length > 0 && bantuan && (
        <div
          id="peta-tombol-multi"
          role="group"
          aria-labelledby="peta-tombol-multi-judul"
          className="shrink-0 rounded-lg border border-line bg-surface p-3 text-sm"
        >
          <h2
            id="peta-tombol-multi-judul"
            className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted"
          >
            {t('Keyboard shortcuts')}
          </h2>
          <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
            {PETA_TOMBOL.map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-2">
                <kbd className="rounded border border-line bg-subtle px-1.5 text-xs">{k}</kbd>
                <span className="text-muted">{t(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
